import { ChangeDetectorRef, Component, forwardRef, OnInit } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { TradeOrderFilterType } from "app/bfx-interface-controls/helpers/TradeTypeFilterHelper";
import { SysFeatures, SystemConfigPromiseService } from "shared/auth-services";
import {
  DEFAULT_CHANGE_CALLBACK,
  DEFAULT_TOUCH_CALLBACK,
  OnChangeCallbackType,
  OnTouchCallbackType,
} from "shared/base-components/models/ControlValueAccessorTypes";

/**
 * Simple dropdown component for all trade types.
 */
@Component({
  selector: "bfx-trade-type-dropdown",
  template: `
    <bfx-dropdown-button
      [(ngModel)]="valueStr"
      [labels]="tradeFilterTypes"
      (ngModelChange)="tradeFilterChanged()"
      type="secondary"
      emphasis="bold"
      class="trade-type-dropdown"
      title="Trade Type Filter"
    ></bfx-dropdown-button>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BfxTradeTypeDropdownComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class BfxTradeTypeDropdownComponent implements ControlValueAccessor {
  /** ControlValueAccessor callback */
  public onModelChange: OnChangeCallbackType = DEFAULT_CHANGE_CALLBACK;

  /** ControlValueAccessor callback */
  public onModelTouched: OnTouchCallbackType = DEFAULT_TOUCH_CALLBACK;

  protected valueStr: string;

  private value: TradeOrderFilterType;

  /** Used for dropdown */
  public tradeFilterTypes: string[];
  /** Used to map incoming external values */
  public allTradeFilterTypes: string[];
  private valueMap: Map<string, TradeOrderFilterType> = new Map();

  private hasBMs: boolean = false;
  private hasLimits: boolean = false;
  private hasAlgos: boolean = false;

  private initialized: boolean = false;
  private initValue: TradeOrderFilterType;

  constructor(
    private sysConfigService: SystemConfigPromiseService,
    private translate: TranslateService,
    private cd: ChangeDetectorRef,
  ) {
    this.init();
  }

  private init(): void {
    this.allTradeFilterTypes = [
      this.translate.instant("trade.filter.type.all"),
      this.translate.instant("trade.filter.type.limit"),
      this.translate.instant("trade.filter.type.bm"),
      this.translate.instant("trade.filter.type.algo"),
    ];
    this.valueMap.set(this.allTradeFilterTypes[0], TradeOrderFilterType.ALL);
    this.valueMap.set(this.allTradeFilterTypes[1], TradeOrderFilterType.LIMIT);
    this.valueMap.set(this.allTradeFilterTypes[2], TradeOrderFilterType.BENCHMARK);
    this.valueMap.set(this.allTradeFilterTypes[3], TradeOrderFilterType.ALGO);

    Promise.all([
      this.sysConfigService.getSystemConfigBoolean(SysFeatures.benchmarksEnabled),
      this.sysConfigService.getSystemConfigBoolean(SysFeatures.allowSptLimit),
      this.sysConfigService.getSystemConfigBoolean(SysFeatures.algosEnabled),
    ]).then(([hasBMs, hasLimits, hasAlgos]) => {
      this.initialiseTypes(hasBMs, hasLimits, hasAlgos);
    });
  }

  /**
   * Initalise dropdown labels after config has been resolved
   * @param hasBMs
   * @param hasLimits
   * @param hasAlgos
   */
  private initialiseTypes(hasBMs: boolean, hasLimits: boolean, hasAlgos: boolean) {
    // Keep a copy of these in case we restore a value that needs to check
    this.hasBMs = hasBMs;
    this.hasLimits = hasLimits;
    this.hasAlgos = hasAlgos;
    this.initialized = true;

    this.tradeFilterTypes = [this.allTradeFilterTypes[0]];
    if (hasLimits) {
      this.tradeFilterTypes.push(this.allTradeFilterTypes[1]);
    }
    if (hasBMs) {
      this.tradeFilterTypes.push(this.allTradeFilterTypes[2]);
    }
    if (hasAlgos) {
      this.tradeFilterTypes.push(this.allTradeFilterTypes[3]);
    }

    this.setValue(this.initValue);
  }

  private setValue(value: TradeOrderFilterType) {
    if (!Number.isInteger(value) || value < 0 || value > this.allTradeFilterTypes.length) {
      value = TradeOrderFilterType.ALL;
    } else if (
      (value == TradeOrderFilterType.ALGO && !this.hasAlgos) ||
      (value == TradeOrderFilterType.BENCHMARK && !this.hasBMs) ||
      (value == TradeOrderFilterType.LIMIT && !this.hasLimits)
    ) {
      value = TradeOrderFilterType.ALL;
    }
    // GEt the correct string value for the enum
    const oldValue = this.value;
    this.value = value;
    this.valueStr = this.allTradeFilterTypes[value];
    if (oldValue !== value) {
      this.onModelChange(this.value);
    }
  }

  writeValue(value: TradeOrderFilterType): void {
    if (this.initialized) {
      this.setValue(value);
    } else {
      // Values can be overridden by defaults coming out of the dropdown
      // through tradeFilterChanged
      this.initValue = value;
    }
  }

  registerOnChange(fn: OnChangeCallbackType): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: OnTouchCallbackType): void {
    this.onModelTouched = fn;
  }

  /**
   * Process a change in the dropdown
   */
  protected tradeFilterChanged() {
    if (this.valueMap.has(this.valueStr)) {
      const newValue = this.valueMap.get(this.valueStr);
      if (newValue !== this.value) {
        this.value = newValue;
        this.onModelChange(this.value);
      }
    }
  }
}
